import React from "react"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import {
  type Language,
  type TranslationContextType,
  availableLanguages,
  DEFAULT_LANGUAGE,
  createTranslationFunction,
  getInitialLanguage,
  saveLanguage,
} from "../lib/i18n"

const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

interface TranslationProviderProps {
  children: ReactNode
}

export function TranslationProvider({ children }: TranslationProviderProps) {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE)

  // Load saved language preference on mount
  useEffect(() => {
    const initialLanguage = getInitialLanguage()
    setLanguageState(initialLanguage)
  }, [])

  // Save language preference when it changes
  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    saveLanguage(lang)
  }

  // Create translation function for current language
  const t = createTranslationFunction(language)

  return (
    <TranslationContext.Provider
      value={{
        language,
        setLanguage,
        t,
        availableLanguages,
      }}
    >
      {children}
    </TranslationContext.Provider>
  )
}

export function useTranslation() {
  const context = useContext(TranslationContext)
  if (context === undefined) {
    throw new Error("useTranslation must be used within a TranslationProvider")
  }
  return context
}

// Helper hook for shorter syntax
export function useT() {
  const { t } = useTranslation()
  return t
}
